import React, {Component} from 'react';
import Assignment from './steps/Assignment';
import Download from './steps/Download';
import CustomerRules from './CustomerRules';
import Upload from './steps/Upload';
import HeaderSteps from './HeaderSteps';
import NotificationSystem from 'react-notification-system';
import Modal from 'react-modal';
import RuleUpdateModal from './rules/RuleUpdateModal';
import {customerApi} from '../../api';

Modal.setAppElement('#root');

class Customer extends Component {

    invoiceInternalId = 0;

    invoiceItemInternalId = 0;

    constructor(props) {
        super(props);

        this.state = {
            company: null,
            config: null,
            currentStep: 1,

            onlineszamlaToken: false,
            showRulesPage: false,
            editRule: null,

            invoices: [],
            customerRules: [],
            vatCollectorCodes: [],
        };

        this.notificationSystemRef = React.createRef();
    }

    componentDidMount() {
        window.scrollTo(0, 0);

        this.loadCustomer();
    }

    getCompanyId = () => {
        return this.props.location.match.params.id;
    };

    loadCustomer = async () => {
        try {
            const data = await customerApi.initialize(
                this.getCompanyId(),
            );

            this.setState({
                company: data.company,
                config: data.config,
                vatCollectorCodes: data.vatCollectorCodes || [],
                customerRules: data.customerRules || [],
                onlineszamlaToken: Boolean(
                    data.onlineszamlaToken,
                ),
            });
        } catch (error) {
            /*
             * A globális hibakezelés már lefutott.
             * Itt csak komponensspecifikus reakció szükséges.
             */
            console.error(
                'Customer initialization failed:',
                error,
            );
        }
    };

    notifySuccess = (message) => {
        if (!this.notificationSystemRef.current) {
            return;
        }

        this.notificationSystemRef.current.addNotification({
            message,
            level: 'success',
            position: 'tc',
            autoDismiss: 10,
        });
    };

    notifyError = (message) => {
        if (!this.notificationSystemRef.current) {
            return;
        }

        this.notificationSystemRef.current.addNotification({
            message,
            level: 'error',
            position: 'tc',
            autoDismiss: 10,
        });
    };

    setStep = (currentStep) => {
        this.setState({
            currentStep,
        });
    };

    nextStep = () => {
        this.setState((previousState) => ({
            currentStep: previousState.currentStep + 1,
        }));
    };

    toggleRulesPage = () => {
        this.setState((previousState) => ({
            showRulesPage: !previousState.showRulesPage,
        }));
    };

    updateRules = (customerRules) => {
        this.updateInvoices(
            this.state.invoices,
            customerRules,
        );
    };

    ensureInternalIds = (invoices) => {
        return (invoices || []).map(
            (invoice) => ({
                ...invoice,
                _internalId: invoice._internalId != null
                    ? invoice._internalId
                    : ++this.invoiceInternalId,

                items: (invoice.items || []).map(
                    (invoiceItem) => ({
                        ...invoiceItem,
                        _internalId: invoiceItem._internalId != null
                            ? invoiceItem._internalId
                            : ++this.invoiceItemInternalId,
                    }),
                ),
            }),
        );
    };

    updateInvoices = (invoices, customerRules) => {
        let addRuleCount = 0;

        const invoicesWithInternalIds =
            this.ensureInternalIds(invoices);

        const updatedInvoices = invoicesWithInternalIds.map(
            (invoice) => {
                const updatedItems = (invoice.items || []).map(
                    (invoiceItem) => {
                        const selectedRule = this.invoiceItemFindRule(
                            invoice,
                            invoiceItem,
                            customerRules || [],
                        );

                        if (selectedRule) {
                            if (
                                invoiceItem.rule_id
                                !== selectedRule.id
                            ) {
                                addRuleCount += 1;
                            }

                            return {
                                ...invoiceItem,
                                rule_id: selectedRule.id,
                            };
                        }

                        if (invoiceItem.rule_id) {
                            return {
                                ...invoiceItem,
                                rule_id: null,
                            };
                        }

                        return invoiceItem;
                    },
                );

                return {
                    ...invoice,
                    items: updatedItems,
                };
            },
        );

        if (addRuleCount > 0) {
            this.notifySuccess(
                `${addRuleCount} darab sor sikeresen kontírozva.`,
            );
        }

        this.setState({
            invoices: updatedInvoices,
            customerRules: customerRules || [],
        });
    };

    invoiceItemFindRule = (
        invoice,
        invoiceItem,
        customerRules,
    ) => {
        return customerRules.find(
            (rule) => this.invoiceCheckRule(
                invoice,
                invoiceItem,
                rule,
            ),
        ) || null;
    };

    invoiceCheckRule = (
        invoice,
        invoiceItem,
        rule,
    ) => {
        const itemName = invoiceItem.name || '';
        const invoiceNumber = invoice.invoiceNumber || '';
        const orderNumberStat = invoice.orderNumberStat || '';
        const positionNumber = invoice.positionNumber || '';
        const positionNumberCountry =
            invoice.positionNumberCountry || '';

        const itemNameMatches =
            rule.item_name === null
            || itemName
                .toLowerCase()
                .includes(rule.item_name.toLowerCase());

        const invoiceNumberMatches =
            rule.invoice_number === null
            || rule.invoice_number === ''
            || invoiceNumber
                .toLowerCase()
                .includes(rule.invoice_number.toLowerCase());

        const orderNumberStatMatches =
            rule.order_number_stat === null
            || rule.order_number_stat === ''
            || orderNumberStat
                .toLowerCase()
                .includes(rule.order_number_stat.toLowerCase());

        const companyTypeMatches =
            rule.company_type === invoice.company_type;

        const vatMatches =
            parseInt(rule.vat, 10) === parseInt(invoiceItem.vat, 10);

        const positionNumberMatches =
            rule.position_number === null
            || rule.position_number === ''
            || positionNumber
                .toLowerCase()
                .includes(rule.position_number.toLowerCase());

        const positionNumberCountryMatches =
            rule.position_number_country === null
            || rule.position_number_country === ''
            || positionNumberCountry
                .toLowerCase()
                .includes(
                    rule.position_number_country.toLowerCase(),
                );

        return (
            itemNameMatches
            && invoiceNumberMatches
            && orderNumberStatMatches
            && companyTypeMatches
            && vatMatches
            && positionNumberMatches
            && positionNumberCountryMatches
        );
    };

    updateConfig = (config) => {
        this.setState({
            config,
        });
    };

    editRule = (rule) => {
        this.setState({
            editRule: rule,
        });
    };

    renderLoading = () => {
        return (
            <section id="customer-upload">
                <NotificationSystem
                    ref={this.notificationSystemRef}
                />
            </section>
        );
    };

    renderRulesPage = () => {
        return (
            <CustomerRules
                config={this.state.config}
                customerRules={this.state.customerRules}
                vatCollectorCodes={
                    this.state.vatCollectorCodes
                }
                toggleRulesPage={
                    this.toggleRulesPage
                }
                updateRules={
                    this.updateRules
                }
                editRule={
                    this.editRule
                }
                notifySuccess={
                    this.notifySuccess
                }
                notifyError={
                    this.notifyError
                }
            />
        );
    };

    renderUploadStep = () => {
        if (this.state.currentStep !== 1) {
            return null;
        }

        return (
            <div className="container">
                <div className="row">
                    <div className="col-md-10 offset-md-1">
                        <Upload
                            config={this.state.config}
                            onlineszamlaToken={
                                this.state.onlineszamlaToken
                            }
                            customerRules={
                                this.state.customerRules
                            }
                            nextStep={
                                this.nextStep
                            }
                            updateInvoices={
                                this.updateInvoices
                            }
                            updateConfig={
                                this.updateConfig
                            }
                            notifySuccess={
                                this.notifySuccess
                            }
                            notifyError={
                                this.notifyError
                            }
                        />
                    </div>
                </div>
            </div>
        );
    };

    renderAssignmentStep = () => {
        if (this.state.currentStep !== 2) {
            return null;
        }

        return (
            <Assignment
                config={this.state.config}
                invoices={this.state.invoices}
                vatCollectorCodes={
                    this.state.vatCollectorCodes
                }
                customerRules={
                    this.state.customerRules
                }
                nextStep={
                    this.nextStep
                }
                updateInvoices={
                    this.updateInvoices
                }
                updateRules={
                    this.updateRules
                }
                notifySuccess={
                    this.notifySuccess
                }
                notifyError={
                    this.notifyError
                }
            />
        );
    };

    renderDownloadStep = () => {
        if (this.state.currentStep !== 3) {
            return null;
        }

        return (
            <Download
                config={this.state.config}
                invoices={this.state.invoices}
                notifySuccess={
                    this.notifySuccess
                }
                notifyError={
                    this.notifyError
                }
            />
        );
    };

    renderCompanyHeader = () => {
        return (
            <div className="header mb-5">
                <div
                    className="container"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <h1>
                        Vevő számlák kontírozása
                    </h1>

                    <button
                        type="button"
                        className="table-green-btn"
                        onClick={this.toggleRulesPage}
                    >
                        Szabályok
                    </button>

                </div>
            </div>
        );
    };

    renderMainContent = () => {
        return (
            <div>
                <HeaderSteps
                    currentStep={this.state.currentStep}
                    setStep={this.setStep}
                />

                {this.renderCompanyHeader()}

                <div className="upload-body">
                    {this.renderUploadStep()}
                    {this.renderAssignmentStep()}
                    {this.renderDownloadStep()}
                </div>
            </div>
        );
    };

    renderRuleUpdateModal = () => {
        if (!this.state.editRule) {
            return null;
        }

        return (
            <RuleUpdateModal
                config={this.state.config}
                rule={this.state.editRule}
                vatCollectorCodes={
                    this.state.vatCollectorCodes
                }
                editRule={
                    this.editRule
                }
                updateRules={
                    this.updateRules
                }
                notifySuccess={
                    this.notifySuccess
                }
                notifyError={
                    this.notifyError
                }
            />
        );
    };

    render() {
        if (!this.state.company) {
            return this.renderLoading();
        }

        return (
            <section id="customer-upload">
                {this.state.showRulesPage
                    ? this.renderRulesPage()
                    : this.renderMainContent()
                }

                {this.renderRuleUpdateModal()}

                <NotificationSystem
                    ref={this.notificationSystemRef}
                />
            </section>
        );
    }
}

export default Customer;


// WEBPACK FOOTER //
// ./src/components/customer/Customer.js